import { Divider, FormGroup } from '@blueprintjs/core'
import * as React from 'react'
import CategoryPicker from '../../../../components/Categories/CategoryPicker'
import config from '../../../../config'
import { UprightStyle } from '../../../../types'
import VariantsSection from '../../../products_v5/components/JoinedForm/ProductForm/VariantsSection'
import ShopifyForm, { ShopifyListingForm } from '../../../products_v5/components/JoinedForm/ShopifyCard/ShopifyForm'
import { FormError, onVariantChangeHandler } from '../../../products_v5/components/JoinedForm/types'
import BasicProductInfo from '../../components/BasicProductInfo'
import ListingStrategyPicker from '../../components/ListingStrategyPicker'
import { ProductOverridesInfo } from '../../../../components/products/ProductOverridesInfo'

interface Props {
  form: ShopifyListingForm
  product: any
  onFormChange: any
  errors?: FormError
  onProductChange: any
  onCategoryChange: any
  onListingStrategyChange: any
  listingStrategyDisabled?: boolean
  onVariantChange: onVariantChangeHandler
  syncProductTags: boolean
  fetchListing?: () => void
  onSkuParseLoading?: (loading: boolean) => void
}

const style: UprightStyle = {
  productFormGrid: { display: 'grid', gridTemplateColumns: '65%' },
  divider: { margin: '20px 0 10px' },
  strategyPickerContainer: { margin: '20px auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' },
  formContainer: { marginBottom: 16 }
}

export const ShopifyFormV4: React.FC<Props> = props => {
  const { category } = props.product || {}
  const productTypeFallback = config.SHOPIFY_PRODUCT_TYPE_INCLUDES_PARENTS() ? category?.pathCache : category?.name

  const { listingStrategyDisabled, product, onListingStrategyChange } = props
  const isRepricing = product?.isMedia === true && product?.isRepricingPaused === false
  const isDisabled = listingStrategyDisabled || isRepricing

  return (
    <div style={style.formContainer}>
      <h4>Product Info</h4>

      <div style={style.productFormGrid}>
        <FormGroup label="Category">
          <CategoryPicker
            onChange={props.onCategoryChange}
            simpleValue={false}
            value={props.product.categoryId}
            width="auto"
          />
        </FormGroup>
        <BasicProductInfo
          onChange={props.onProductChange}
          parseSkuQRCodePayloads
          onSkuParseLoading={props.onSkuParseLoading}
          {...props.product}
        />
      </div>

      {config.SHOPIFY_VARIANTS() && (
        <>
          <Divider style={{ ...style.divider, marginBottom: 20 }} />
          <div>
            <VariantsSection
              errors={props.errors}
              variants={props.product.variants}
              product={props.product}
              onProductChange={e => props.onProductChange(e.name, e.value)}
              onVariantChange={props.onVariantChange}
              draft={false}
            />
          </div>
        </>
      )}

      <Divider style={style.divider} />
      <h4>Listing Strategy</h4>
      <div style={{ margin: '20px auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <ListingStrategyPicker
            onChange={v => onListingStrategyChange(v)}
            value={product?.listingStrategyId}
            disabled={isDisabled}
            title="Listing Strategy"
          />
          {isRepricing && (
            <div style={{ marginTop: '1px', fontSize: '12px', color: '#666' }}>This is being repriced.</div>
          )}
        </div>
      </div>

      <Divider style={style.divider} />
      <h4>Shopify Info</h4>
      {!!props.product?.overriddenValues?.overrideProperties && (
        <ProductOverridesInfo
          overrideProperties={props.product.overriddenValues.overrideProperties}
          productId={props.product.id}
          onSuccess={() => props.fetchListing()}
        />
      )}
      <ShopifyForm
        form={props.form}
        syncProductTags={props.syncProductTags}
        hasVariants={props.product?.variants?.length > 0}
        disablePriceInputs={!!props.product?.overriddenValues}
        onFormChange={props.onFormChange}
        // for pre-populating the productType field
        productTypeFallback={productTypeFallback || ''}
        skipErrorPrefix // errors do not have shopifyListing prefix like on JoinedForm
        errors={props.errors || {}}
        productUnitTracking={!!props.product.unitTracking}
      />
    </div>
  )
}
